import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, FileText, UploadCloud } from 'lucide-react';
import GreetingHeader from '@components/dashboard/GreetingHeader';
import StatCard from '@components/dashboard/StatCard';
import QuickActionCard from '@components/dashboard/QuickActionCard';
import RecentDocumentsCard from '@components/dashboard/RecentDocumentsCard';
import AnnouncementsCard from '@components/dashboard/AnnouncementsCard';
import { DASHBOARD_QUICK_ACTIONS } from '@constants/dashboard';
import { selectDesignVerificationEnabled } from '@features/featureFlags/featureFlagsSlice';
import { useAppSelector } from '@store/index';
import { ANNOUNCEMENT_SEED } from '@constants/announcements';
import { useDashboardData } from '@hooks/useDashboardData';
import type { Announcement } from '../types/dashboard';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { stats, recentDocuments, loading } = useDashboardData();
  const designVerificationEnabled = useAppSelector(selectDesignVerificationEnabled);

  // `null` is still-loading: keep the card, or it pops in after the flag lands.
  const quickActions = useMemo(
    () =>
      DASHBOARD_QUICK_ACTIONS.filter(
        (action) => !action.requiresDesignVerification || designVerificationEnabled !== false,
      ),
    [designVerificationEnabled],
  );

  const announcements = useMemo<Announcement[]>(
    () =>
      ANNOUNCEMENT_SEED.map(({ id, key, type, priority, daysAgo }) => ({
        id,
        title: t(`dashboard.announcementItems.${key}.title`),
        message: t(`dashboard.announcementItems.${key}.message`),
        type,
        priority,
        author: t('dashboard.announcementAuthor'),
        createdAt: new Date(Date.now() - daysAgo * 86_400_000).toISOString(),
      })),
    [t],
  );

  const lastUpdated = useMemo(() => {
    if (!stats.lastUpdated) return '—';
    const date = new Date(stats.lastUpdated);
    return Number.isNaN(date.getTime())
      ? '—'
      : date.toLocaleDateString(i18n.language?.startsWith('ja') ? 'ja-JP' : 'en-US', {
          month: 'short',
          day: 'numeric',
        });
  }, [stats.lastUpdated, i18n.language]);

  return (
    <div className="@container/page flex flex-col gap-6">
      <GreetingHeader onUpload={() => navigate('/documents/upload')} />

      <div className="grid gap-4 @md/page:grid-cols-2 @4xl/page:grid-cols-3">
        <StatCard
          label={t('dashboard.totalDocuments')}
          value={stats.totalDocuments}
          icon={FileText}
          hint={t('dashboard.allTime')}
          loading={loading}
        />
        <StatCard
          label={t('dashboard.recentUploads')}
          value={stats.recentUploads}
          icon={UploadCloud}
          hint={t('dashboard.inLast7Days')}
          loading={loading}
        />
        <StatCard
          label={t('dashboard.lastUpdated')}
          value={lastUpdated}
          icon={Clock}
          hint={t('dashboard.latestRecord')}
          loading={loading}
          className="@md/page:col-span-2 @4xl/page:col-span-1"
        />
      </div>

      <section className="grid gap-3 @md/page:grid-cols-2 @4xl/page:grid-cols-4">
        <h2 className="sr-only">{t('dashboard.quickActionsTitle')}</h2>
        {quickActions.map((action) => (
          <QuickActionCard key={action.path} action={action} />
        ))}
      </section>

      <div className="grid gap-4 @4xl/page:grid-cols-[1.5fr_1fr]">
        <RecentDocumentsCard
          documents={recentDocuments}
          loading={loading}
          onSelect={(id) => navigate(`/documents/${id}`)}
          onViewAll={() => navigate('/documents')}
          onUpload={() => navigate('/documents/upload')}
        />
        <AnnouncementsCard announcements={announcements} loading={loading} />
      </div>
    </div>
  );
};

export default DashboardPage;
